import { GraduationCap, IdCard, User } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'

import userService from '@/services/user.service'

import { UserProfile } from '@/types/user.type'

interface UserProfileHoverCardProps {
  userId: number
  profileId?: string
  children: React.ReactNode
}

export const UserProfileHoverCard: React.FC<UserProfileHoverCardProps> = ({
  userId: _userId,
  profileId,
  children,
}) => {
  const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const fetchUserProfile = React.useCallback(async () => {
    if (!profileId) return

    try {
      setIsLoading(true)
      setError(null)
      const profile = await userService.getUser(profileId)
      setUserProfile(profile)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user profile:', err)
      setError('Failed to load profile')
    } finally {
      setIsLoading(false)
    }
  }, [profileId])

  const handleOpenChange = (open: boolean) => {
    if (open && !userProfile && !isLoading) {
      fetchUserProfile()
    }
  }

  return (
    <HoverCard openDelay={300} closeDelay={200} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-80 p-4 max-h-96 overflow-y-auto" side="top" align="start">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-sm text-red-500 text-center py-4">{error}</div>
        ) : userProfile ? (
          <div className="space-y-4">
            {/* User Header */}
            <div className="flex items-start gap-3">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {userProfile.avatar ? (
                    <Image
                      src={userProfile.avatar}
                      alt={`${userProfile.firstName} ${userProfile.lastName}`}
                      width={56}
                      height={56}
                      className="w-14 h-14 object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6 text-gray-400" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base text-gray-900 truncate">
                  {userProfile.firstName} {userProfile.lastName}
                </h3>
                <p className="text-sm text-gray-500">{userProfile.email}</p>
              </div>
            </div>

            {/* Profile ID */}
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <IdCard className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono text-gray-900 truncate">{userProfile.profileId}</p>
              </div>
            </div>

            {/* Enrolled Courses */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-gray-500" />
                <h4 className="text-sm font-medium text-gray-700">Enrolled Courses</h4>
              </div>
              <div className="pl-6">
                {userProfile.courseUsers && userProfile.courseUsers.length > 0 ? (
                  <div className="space-y-2">
                    {userProfile.courseUsers.map(courseUser => (
                      <div key={courseUser.id} className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium">{courseUser.course.title}</p>
                          <p className="text-xs text-gray-500">
                            Enrolled {new Date(courseUser.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No courses enrolled</p>
                )}
              </div>
            </div>

            {/* Certificates */}
            {userProfile.courseCertifications && userProfile.courseCertifications.length > 0 && (
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    🏆 {userProfile.courseCertifications.length} Certificate
                    {userProfile.courseCertifications.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500 text-center py-4">No profile data available</div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
